/*
 * Copyright (c) 2024. Spin in het Web B.V. Apeldoorn NL.
 * Deze software is géén open source. Het auteursrecht van deze software
 * ligt bij Spin in het Web B.V. en/of Jellybean B.V. en/of de auteur.
 * Gebruik van deze software is onderhevig aan een licentieovereenkomst.
 * www.spininhetweb.nl
 */

import {Injectable} from '@angular/core';
import {LocalConfig} from "../localconfig";
import {Subject, Subscription} from "rxjs";
import {hash} from "../types";
import {io, Socket} from "socket.io-client";
import {Sihwlog} from "../sihwlog";

/**
 * Maximale wachttijd (ms) op een antwoord, als de localconfig er niets over zegt. Gelijk aan wat
 * deze service altijd deed: een eerste controle na 20 s en daarna elke 5 s, 24 keer.
 */
const STANDAARD_MAX_WACHTTIJD = 140000;

/**
 * Wachttijd (ms) op een antwoord als de socket bij het versturen nog niet verbonden was. De emit
 * wordt door socket.io gebufferd en gaat mee zodra de verbinding er is; loopt deze tijd af zonder
 * dat dat gebeurt, dan is het request kansloos. Op een trage lijn mag dit ruimer, zie
 * `SIHW.SpinJS.opstartWachttijd`.
 */
const STANDAARD_OPSTART_WACHTTIJD = 10000;

/**
 * Na hoeveel ms er bij een verbonden socket voor het eerst gekeken wordt of er al antwoord is.
 */
const EERSTE_CONTROLE = 20000;

/**
 * Interval (ms) van de controles daarna.
 */
const CONTROLE_INTERVAL = 5000;

/**
 * Opties per verzending. `timeout` is de maximale wachttijd in milliseconden voor dit ene request;
 * zonder waarde geldt de wachttijd van de service (`SIHW.SpinJS.maxWachttijd`). Bedoeld voor calls
 * waarvan het backend zelf weet dat ze lang mogen duren en dat per gebruiker of project instelt —
 * de client mag dan niet eerder opgeven dan het backend.
 */
export interface VerzendOpties {
  timeout?: number;
}

@Injectable({
  providedIn: 'root'
})
export class SpinJS {
  //Subjects
  protected internalEvents = new Subject<{ event: string, data?: any }>(); //emit bij connect etc
  protected backendNotify = new Subject<{ msg: string, data?: any }>(); //emit bij backend-notify (voor zover mogelijk)
  protected whenConnectedResolvers: Function[] = [];
  private conf = new LocalConfig();
  private log: Sihwlog = new Sihwlog(); //tijdelijk, wordt in initservice overschreven
  private socket!: Socket;
  /**
   * Emit bij elke nieuwe connect. Zie ook waitconnected voor een promise
   * @private
   */
  private auth = {
    storekey: <string>'', persistence: <string>'', store: <hash<any>>{}
  };

  /**
   * Maximale wachttijd (ms) op een antwoord, uit `SIHW.SpinJS.maxWachttijd`
   */
  private maxWachttijd: number = STANDAARD_MAX_WACHTTIJD;

  /**
   * Wachttijd (ms) op een antwoord dat verstuurd is voordat de socket verbonden was, uit
   * `SIHW.SpinJS.opstartWachttijd`
   */
  private opstartWachttijd: number = STANDAARD_OPSTART_WACHTTIJD;

  /**
   * Resolvet zodra de configuratie gelezen is en de socket is opgezet
   */
  private readonly gereed: Promise<void>;

  constructor() {
    this.gereed = this.startService();
  }

  private _url!: string;

  /////////////////////////////////////////////// URL //////////////////////////////////////////
  /**
   * Return de gebruikte url
   */
  public get url(): string {
    return this._url;
  }

  /**
   * Zijn we op dit moment verbonden?
   */
  public get connected(): boolean {
    return this.socket && this.socket.connected;
  }

  /**
   * Geeft de huidige authkey terug, of false als die er niet is
   */
  public get authKey(): string | null {
    return this.auth.store[this.auth.storekey] || null;
  }

  /////////////////////////////// AUTH //////////////////////////////////////////////
  /**
   * Stel een nieuwe authkey in voor gebruik vanaf nu. Deze key wordt ook opgeslagen in de authstorage
   * @param authkey
   */
  public set authKey(authkey: string | null) {
    if (authkey) {
      this.auth.store[this.auth.storekey] = authkey;
    } else {
      delete this.auth.store[this.auth.storekey];
    }
  }


  /**
   * Verwijder de authkey zodat iemand uitgelogd is
   */
  public deleteAuth() {
    this.authKey = null;
  }

  /**
   * Geef terug of er een authkey is
   */
  public get hasAuth(): boolean {
    return !!this.authKey;
  }

  /**
   * Return de url voor get-requests (is zelfde als url)
   */
  public get getUrl(): string {
    return this.url;
  }

  ///////////////////////////// STATUS /////////////////////////////////////////
  /**
   * Geef een promise die resolvet zodra de service zichzelf heeft opgezet: de configuratie is
   * gelezen en de socket bestaat. Dit is nadrukkelijk niet hetzelfde als verbonden zijn — daarvoor
   * is `whenConnected`. Nodig voor een lezer die vóór de eerste verzending al wil weten of er een
   * authkey is; `send` wacht er zelf op.
   */
  public whenReady(): Promise<void> {
    return this.gereed;
  }

  /**
   * Geef een promise die resolvet als we verbinding (her)krijgen met het backend
   * @return {*}
   */
  public whenConnected(): Promise<void> {
    if (this.connected) {
      return Promise.resolve(); //meteen al klaar, want we zijn nu verbonden
    }
    this.log.debug('whenConnected geeft promise terug die nog niet resolved is');
    return new Promise(resolve => {
      this.whenConnectedResolvers.push(resolve); //bewaar deze resolver voor als we connected zijn
    });
  }

  ////////////////////////// Observables /////////////////////////////////////////////
  /**
   * Subscribe op interne events (close etc)
   * @param observer
   */
  public onInternal(observer: any): Subscription {
    return this.internalEvents.subscribe(observer);
  };

  /**
   * Subscribe op backend notificaties (let op: is een noop bij rest: nooit een event)
   * @param observer
   */
  public onNotify(observer: any): Subscription {
    return this.backendNotify.subscribe(observer);
  }

  /**
   * Maak een complete get-url met de gegeven argumenten en eventueel een authkey
   * @param controller
   * @param actie
   * @param args Argumentenarray, voeg eventueel een extra arg toe om een mooie titel in een browserscherm te krijgen
   * @param [metAuth]
   */
  public maakGetUrl(controller: string, actie: string, args: string[], metAuth?: boolean): string {
    let volUrl = this._url;
    if (volUrl[volUrl.length - 1] !== '/') {
      volUrl += '/';
    }
    volUrl += `${controller}/${actie}`;
    if (metAuth) {
      volUrl += '/' + encodeURIComponent(`a=${this.authKey || ''}`);
    }
    volUrl += args.map(arg => `/${encodeURIComponent(arg)}`).join('');
    return volUrl;
  }

  ////////////////////////////////// ZENDEN //////////////////////////////
  /**
   * Doe een ping-request volgens het spinjs-protocol. Return de promise die dus zal resolven of rejecten
   * Via WS in de normale SpinJS2 lib, via post in de Rest-variant
   */
  public ping(): Promise<any> {
    return this.send('spinjs2', 'ping', {});
  }

  /**
   * Communiceer met het SpinJS2 Backend, via WS of Post
   * @param controller
   * @param action
   * @param data
   * @param [opties] Opties voor deze ene verzending, zie VerzendOpties
   */

  public async send(controller: string, action: string, data: { [key: string]: any } = {},
                    opties: VerzendOpties = {}): Promise<any> {

    //speciaal: als data['_spinjs2_slowdebug'] een waarde heeft, dan is dat een timeout voor deze send
    //om slome lijnen te testen

    await this.gereed; //de configuratie en de socket moeten er zijn; dit wacht niet op verbinding

    return new Promise<any>((resolve, reject) => {
      if (!this._url) {
        return reject('Geen url');
      }

      if (data['_spinjs2_slowdebug']) {
        let vertraging = data['_spinjs2_slowdebug'];
        delete data['_spinjs2_slowdebug'];
        this.log.warn('SPINJS2 SLOW DEBUG: send met vertraging', vertraging, controller, action);
        setTimeout(() => {
          this.send(controller, action, data, opties).then(resolve, reject);
        }, vertraging);
        return;
      }

      let emitstate = 'init'; //we houden het een en ander bij

      //hoe lang we op een antwoord wachten: de instelling van de service, of wat deze ene call meegeeft
      const maxWachttijd = opties.timeout ?? this.maxWachttijd;
      const verzondenOp = Date.now();

      //zet een timeout op de verzending. Wat we daarmee doen hangt af van de verbindingsstatus
      let timeout = Math.min(EERSTE_CONTROLE, maxWachttijd); //als we nu verbonden zijn, kijken we pas over lange tijd of alles goed is. Wel kan het zijn dat de verbinding al weg is, maar de socket dat nog niet weet (verbindingsprobleem aan client-kant)

      if (!this.socket.connected) {
        //kijk of een tijdje hoe we er voor staan
        this.log.info('SPINJS2: socket nog niet verbonden tijdens send. We sturen het bericht, maar kijken zo of de socket er al is');
        timeout = this.opstartWachttijd; //genoeg voor hobbeltjes; op een trage lijn ruimer te zetten
      }

      let timoutHandler = () => {
        //al klaar?
        if (emitstate === 'antwoord') {
          return; //we zijn klaar
        }
        this.log.debug(`Check send timeout ${this.socket.connected ? 'connected' : 'disconnected'} Emitstate: ${emitstate}`);
        //als we niet connected zijn, geven we een timeout, dan is het gewoon kansloos
        if (!this.socket.connected) {
          emitstate = 'skip'; //mocht het ooit nog komen
          this.log.warn('SPINJS2: Timeout connecting');
          reject('timeout connecting');
        } else if (Date.now() - verzondenOp >= maxWachttijd) {
          emitstate = 'skip'; //we geven op
          this.log.warn('SPINJS2: Timeout receiving');
          reject('timeout receiving');
        } else {
          //wel verbonden, nog geen antwoord. Gewoon straks weer proberen
          setTimeout(timoutHandler, CONTROLE_INTERVAL);
        }
      };

      setTimeout(timoutHandler, timeout);

      let auth = this.authKey;
      this.log.debug(`SpinJS2: WS => ${controller}.${action} ${(!!auth) ? 'auth' : 'noauth'}`, data);
      let res = new Promise((resolve, reject) => {
        this.socket.emit('apireq', {
          controller: controller, action: action, auth: auth, data: data
        }, (resultaat: any, rdata: any) => {
          if (resultaat) {
            resolve(rdata);
          } else {
            reject(rdata);
          }
        });

      });

      emitstate = 'verzonden'; //wacht op antwoord
      res.then((rdata: any) => {

        if (emitstate === 'skip') {
          return; //promise al rejected door de timeout
        }
        emitstate = 'antwoord'; //we hebben antwoord
        this.log.debug('SpinJS2: RESULTAAT <= ' + controller + '.' + action, rdata);
        try {
          if (rdata._newauth) {
            //alleen als we er al eentje hadden (opnieuw checken, want async)
            if (this.hasAuth) {
              this.log.debug('SPINJS2 we hebben een nieuw auth');
              this.authKey = rdata._newauth; //dit is de nieuwe
              delete rdata._newauth;
            } else {
              this.log.warn('SPINJS2 we krijgen een nieuwe auth, maar hadden er geen');
            }
          }
        } catch (_e) {
          //blijkbaar is rdata geen object? Whatever
          this.log.debug('SPINJS2 reply kon niet gecheckt worden op _newauth');
        }
        resolve(rdata); //teruggeven
      })
        .catch(rdata => {
          emitstate = 'antwoord'; //we hebben antwoord
          this.log.warn(`SpinJS2: Error <= ${controller}.${action}`, rdata);
          reject(rdata);
        });
    });
  }

  /**
   * Zet de service op zodra de configuratie er is. Is die al geladen — het normale geval bij een
   * APP_INITIALIZER — dan gebeurt dat meteen, nog binnen de constructor, zodat een vroege lezer van
   * `hasAuth` of `url` niet op een lege service kijkt en de eerste verzending niet op 'Geen url'
   * stukloopt.
   */
  private startService(): Promise<void> {
    if (this.conf.loaded) {
      this.initService();
      return Promise.resolve();
    }
    return this.conf.whenLoaded.then(() => this.initService());
  }

  private initService() {
    this.log = new Sihwlog(this.conf.$('SIHW.SpinJS.logLevel', 'warn'), "SpinJS");
    console.log(`SpinJS Angular connector (c) 2024 Jellybean BV Apeldoorn NL`);
    this.maxWachttijd = this.conf.$('SIHW.SpinJS.maxWachttijd', STANDAARD_MAX_WACHTTIJD);
    this.opstartWachttijd = this.conf.$('SIHW.SpinJS.opstartWachttijd', STANDAARD_OPSTART_WACHTTIJD);
    this._url = this.conf.$('SIHW.SpinJS.url');
    if (!this._url) {
      //legacy fallback: probeer SpinJS2 key
      const legacyUrl = this.conf.$('SIHW.SpinJS2.url');
      if (legacyUrl) {
        console.warn(`SpinJS: SIHW.SpinJS.url niet gevonden in localconfig, legacy key SIHW.SpinJS2.url wordt gebruikt. Pas localconfig.json aan.`);
        this._url = legacyUrl;
      } else {
        this.log.error(`geen url geconfigureerd in localconfig SIHW.SpinJS.url - stop`);
        return;
      }
    }

    //auth
    this.auth.storekey = this.conf.$('SIHW.SpinJS.storeKey') ?? (() => {
      const legacy = this.conf.$('SIHW.SpinJS2.storeKey');
      return legacy ?? 'SpinJS';
    })();
    this.auth.persistence = this.conf.$('SIHW.SpinJS.authPersistence') ?? (() => {
      const legacy = this.conf.$('SIHW.SpinJS2.authPersistence');
      if (legacy !== undefined) {
        console.warn(`SpinJS: SIHW.SpinJS.authPersistence niet gevonden, legacy key SIHW.SpinJS2.authPersistence wordt gebruikt. Pas localconfig.json aan.`);
      }
      return legacy ?? 'memory';
    })();
    switch (this.auth.persistence) {
      case 'session':
        this.auth.store = window.sessionStorage || {}; //fallback
        break;
      case 'storage':
        this.auth.store = (window.localStorage) || (window.sessionStorage) || {}; //fallbacks
        break;
      case 'memory':
      default:
        this.auth.store = {};
        break;
    }
    this.log.debug(`storage`, this.auth.store === window.localStorage ? 'Local' : (this.auth.store === window.sessionStorage ? 'Session' : 'Mem'));

    ///// INIT SOCKET ////////
    this.log.debug(`verbinding maken met ${this._url}`);
    this.socket = io(this._url);
    //even een scopevar
    let lastSocketEventWasError: boolean = false;
    this.socket.on('connect', () => {
      this.log.debug('Socket connect', {url: this._url});
      if (lastSocketEventWasError)
      {
        this.log.warn("Reconnect");
      }
      lastSocketEventWasError = false;
      this.internalEvents.next({event: 'connect'}); //broadcast
      this.resolveWhenConnected();
    });
    this.socket.on('spinjs:connect', () => {
      this.log.debug('Backend connect');
      lastSocketEventWasError = false;
      this.internalEvents.next({event: 'spinjsconnect'}); //broadcast
      this.resolveWhenConnected();
    });
    this.socket.on('connect_error', (...args: any[]) => {
      if (! lastSocketEventWasError) {
        this.log.warn("Connect error. Waiting for reconnect");
        this.log.debug('error', ...args);
      }
      lastSocketEventWasError = true;
      //we geven het door aan geïnteresseerden
      this.internalEvents.next({event: 'error', data: args}); //broadcast
    });

    this.socket.on('disconnect', (...args: any[]) => {
      this.log.warn("Disconnect from backend");
      this.log.debug('close', ...args);
      lastSocketEventWasError = false;
      this.internalEvents.next({event: 'close'}); //broadcast
    });
//notificatie door het backend
    this.socket.on('notify', (notifydata: any) => {
      this.log.debug('backendNotify', notifydata);
      //stuur het de wereld in:
      this.backendNotify.next(notifydata);
    });
  }

  /**
   * Helper voor whenConnected:
   */
  private resolveWhenConnected(): void {
    for (let F of this.whenConnectedResolvers) {
      F();
    }
    this.whenConnectedResolvers = []; //en weer leeg
  }
}
